import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { EditorContent } from '@tiptap/react';
import { CustomStyles } from '@tyro/core/editor';
import type { MouseEvent } from 'react';
import { useDocumentationPreviewEditor } from '../../utils/markdown-editor';

interface MarkdownViewProps {
  content: string;
  sx?: SxProps<Theme>;
  onDocLinkClick?: (spaceSlug: string, pageSlug: string) => void;
}

const DOCS_HREF_PREFIX = 'docs:';

export function MarkdownView({
  content,
  sx,
  onDocLinkClick,
}: MarkdownViewProps) {
  const editor = useDocumentationPreviewEditor(content);
  if (!editor) return null;

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onDocLinkClick) return;
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a');
    const href = anchor?.getAttribute('href');
    if (!href || !href.startsWith(DOCS_HREF_PREFIX)) return;
    event.preventDefault();
    const slugPair = href.slice(DOCS_HREF_PREFIX.length);
    const [spaceSlug, pageSlug] = slugPair.split('/', 2);
    if (spaceSlug && pageSlug) {
      onDocLinkClick(spaceSlug, pageSlug);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={[
        { ...CustomStyles },
        { '& .ProseMirror': { outline: 'none' } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <EditorContent editor={editor} />
    </Box>
  );
}
